import { IntentSeverityBadge } from '@ipfabric/ui-components/IntentVerification/IntentSeverityBadge';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import {
  type AdvancedFilterTableColumnDefinition,
  INTENT_RULE_CHECK_DESCRIPTION_MAX_LENGTH,
  INTENT_RULE_DESCRIPTION_MAX_LENGTH,
  INTENT_RULE_NAME_MAX_LENGTH,
  type ReportData,
  type ReportSeverity,
  type ReportSeverityKey,
  reportSeverities,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { unsafeSetDeepProp } from '@ipfabric/utils/fp/usafeSetDeepProp';
import classNames from 'classnames';
import { useApiTableContextSelectors } from 'components/ApiTable/model/ApiTableModel';
import { saveIntentVerificationRule } from 'components/ApiTable/model/commands/saveIntentVerificationRule';
import { Button } from 'components/Button/Button';
import { FormikMultiSelectField } from 'components/FormikMultiSelectField/FormikMultiSelectField';
import { FormikSelectField } from 'components/FormikSelectField/FormikSelectField';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { getFromFilters } from 'components/Table/components/AdvancedFilter/getFromFilters';
import {
  type GroupFilter,
  type RuleFilter,
  emptyGroupFilter,
  isGroupFilter,
} from 'components/Table/components/AdvancedFilter/useAdvancedFilter';
import type { RuleContext } from 'components/Table/components/AdvancedFilter/useRuleContext';
import { isFilterInvalid } from 'components/Table/components/AdvancedFilter/validateRule';
import { isBackendColumn } from 'components/Table/model/isBackendColumn';
import { Tabs } from 'components/Tabs/Tabs';
import { type Tab, useTabs } from 'components/Tabs/useTabs';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, useFormik } from 'formik';
import { useMemo } from 'react';
import type { FC } from 'react';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { nestedFormikName } from 'utils/formik/nestedFormikName';
import { trimEntity } from 'utils/trimEntity';
import type { DeepRequired } from 'utils/types/DeepRequired';
import type { DeleteIntentVerificationRow } from './IntentVerification';
import { IntentVerificationRuleCheck } from './IntentVerificationRuleCheck';
import type { IntentVerificationTableRow } from './intentVerificationColumns';

export interface IntentVerificationRuleFormValues {
  id?: string;
  name: string;
  column: string | null;
  groups: string[];
  checks: Record<ReportSeverityKey, GroupFilter>;
  descriptions: DeepRequired<ReportData['descriptions']>;
  defaultColor: ReportData['defaultColor'];
}

const EMPTY_INITIAL_VALUES = {
  name: '',
  column: null,
  groups: [],
  checks: {
    // create a new object to avoid same reference because formik validation
    // will not work properly if the reference is the same
    0: { ...emptyGroupFilter },
    10: { ...emptyGroupFilter },
    20: { ...emptyGroupFilter },
    30: { ...emptyGroupFilter },
  },
  descriptions: {
    general: '',
    checks: {
      0: '',
      10: '',
      20: '',
      30: '',
    },
  },
  defaultColor: null,
};

const getGroupFilter = <TRow,>(
  checks: ReportData['checks'],
  columns: AdvancedFilterTableColumnDefinition<TRow>[],
  severity: ReportSeverityKey,
  ruleContext: RuleContext,
): GroupFilter => {
  const filters = checks[severity];

  if (filters) {
    return getFromFilters({ ...filters }, columns, ruleContext);
  }
  return { ...emptyGroupFilter };
};

export const intentRowToFormValues = <TRow,>(
  row: IntentVerificationTableRow,
  columns: AdvancedFilterTableColumnDefinition<TRow>[],
  ruleContext: RuleContext,
): IntentVerificationRuleFormValues => ({
  id: row.id,
  name: row.name,
  column: row.column,
  groups: row.groups.map(prop('id')),
  checks: {
    0: getGroupFilter(row.checks, columns, '0', ruleContext),
    10: getGroupFilter(row.checks, columns, '10', ruleContext),
    20: getGroupFilter(row.checks, columns, '20', ruleContext),
    30: getGroupFilter(row.checks, columns, '30', ruleContext),
  },
  descriptions: {
    general: '',
    ...row.descriptions,
    checks: {
      0: row.descriptions.checks?.['0'] ?? '',
      10: row.descriptions.checks?.['10'] ?? '',
      20: row.descriptions.checks?.['20'] ?? '',
      30: row.descriptions.checks?.['30'] ?? '',
    },
  },
  defaultColor: row.defaultColor,
});

interface Props {
  className?: string;
  closeForm: () => void;
  initialValues?: IntentVerificationRuleFormValues;
  onDeleteClick?: (row: DeleteIntentVerificationRow) => void;
}

export const IntentVerificationRuleForm: FC<Props> = (props) => {
  const {
    apiTable: {
      intentVerification: {
        form: formMsg,
        actions: actionsMsg,
        heading: headingMsg,
      },
    },
  } = useLocale();
  const ctxProps = useApiTableContextSelectors({
    dispatch: prop('dispatch'),
    reports: prop('reports'),
    columns: prop('tableModel', 'columns'),
    groups: prop('groups'),
  });

  const [loadingState, setLoadingState] = useLoadingState();

  const formik = useFormik({
    initialValues: props.initialValues ?? EMPTY_INITIAL_VALUES,
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
    validate: (values) => {
      const errors: FormikErrors<IntentVerificationRuleFormValues> = {};

      if (!values.name) {
        errors.name = formMsg.required;
      } else if (values.name.length > INTENT_RULE_NAME_MAX_LENGTH) {
        errors.name = formMsg.maxLength(INTENT_RULE_NAME_MAX_LENGTH);
      }

      if (
        values.descriptions.general.length > INTENT_RULE_DESCRIPTION_MAX_LENGTH
      ) {
        unsafeSetDeepProp(
          errors,
          'descriptions',
          'general',
        )(formMsg.maxLength(INTENT_RULE_DESCRIPTION_MAX_LENGTH));
      }

      if (!values.column) {
        errors.column = formMsg.required;
      }

      const isRuleSet = (rule: RuleFilter) => rule.operator !== null;

      const hasGroupAtLeastOneRuleSet = (group: GroupFilter): boolean => {
        return group.filters.some((filter) =>
          isGroupFilter(filter)
            ? hasGroupAtLeastOneRuleSet(filter)
            : isRuleSet(filter),
        );
      };

      const nonEmptyFilters = Object.values(values.checks).filter(
        hasGroupAtLeastOneRuleSet,
      );

      for (const [severity, filter] of Object.entries(values.checks)) {
        if (nonEmptyFilters.length === 0 && values.defaultColor === null) {
          // Turn of typing since FormikErrors are not able to map Record properly and
          // expecting value (GroupFilter) rather than string here
          unsafeSetDeepProp(
            errors,
            'checks',
            severity as ReportSeverityKey,
          )(formMsg.atLeastOneFilter as any);
        } else if (isFilterInvalid(filter)) {
          unsafeSetDeepProp(
            errors,
            'checks',
            severity as ReportSeverityKey,
          )(formMsg.invalidFilter as any);
        }

        if (
          values.descriptions.checks[severity as ReportSeverityKey].length >
          INTENT_RULE_CHECK_DESCRIPTION_MAX_LENGTH
        ) {
          unsafeSetDeepProp(
            errors,
            'descriptions',
            'checks',
            severity as ReportSeverityKey,
          )(formMsg.maxLength(INTENT_RULE_CHECK_DESCRIPTION_MAX_LENGTH));
        }
      }

      return errors;
    },
    onSubmit: makeFormikOnSubmit(async (values) => {
      const trimmedValues = trimEntity(values);
      await ctxProps.dispatch(saveIntentVerificationRule, {
        values: trimmedValues,
      });
      props.closeForm();
    }),
  });

  const tabs: Tab<ReportSeverityKey>[] = [
    {
      key: '0',
      label: (
        <IntentSeverityBadge
          severity='0'
          default={formik.values.defaultColor === 0}
        >
          {formMsg.success}
        </IntentSeverityBadge>
      ),
      render: () => (
        <IntentVerificationRuleCheck severity={0} formik={formik} />
      ),
    },
    {
      key: '10',
      label: (
        <IntentSeverityBadge
          severity='10'
          default={formik.values.defaultColor === 10}
        >
          {formMsg.info}
        </IntentSeverityBadge>
      ),
      render: () => (
        <IntentVerificationRuleCheck severity={10} formik={formik} />
      ),
    },
    {
      key: '20',
      label: (
        <IntentSeverityBadge
          severity='20'
          default={formik.values.defaultColor === 20}
        >
          {formMsg.warning}
        </IntentSeverityBadge>
      ),
      render: () => (
        <IntentVerificationRuleCheck severity={20} formik={formik} />
      ),
    },
    {
      key: '30',
      label: (
        <IntentSeverityBadge
          severity='30'
          default={formik.values.defaultColor === 30}
        >
          {formMsg.error}
        </IntentSeverityBadge>
      ),
      render: () => (
        <IntentVerificationRuleCheck severity={30} formik={formik} />
      ),
    },
  ];

  const tabsHook = useTabs({
    tabs,
    initialActiveTab: '0',
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies(props.initialValues?.column): initial value
  const columnOptions = useMemo(() => {
    const disabledColumns = Object.values(ctxProps.reports)
      .map(prop('column'))
      .filter((c) => c !== props.initialValues?.column);

    return ctxProps.columns
      .filter((column) => isBackendColumn(column) && !column.disabledColorize)
      .map((column) => ({
        label: column.title,
        value: column.key,
        disabled: disabledColumns.includes(column.key),
      }));
  }, [ctxProps.columns, ctxProps.reports]);

  const groupOptions = useMemo(
    () =>
      ctxProps.groups.map((group) => ({
        label: group.name,
        value: group.id,
      })),
    [ctxProps.groups],
  );

  const submitForm = async () => {
    await withLoadingIndicator(setLoadingState)(async () =>
      formik.submitForm(),
    );
    // if there is error in some severity tab, switch to the tab with error
    const erroredSeverities = reportSeverities.filter(
      (severity) =>
        formik.errors.checks?.[severity] ||
        formik.errors.descriptions?.checks?.[severity],
    );
    if (erroredSeverities.length) {
      if (
        tabsHook.activeTab !== null &&
        !erroredSeverities.includes(
          Number(tabsHook.activeTab) as ReportSeverity,
        )
      ) {
        const firstErroredSeverity = erroredSeverities?.[0]?.toString(10);
        if (firstErroredSeverity !== undefined) {
          tabsHook.handleChangeTab(firstErroredSeverity as ReportSeverityKey);
        }
      }
    }
  };

  const id = props.initialValues?.id;

  const nameField = (
    <FormikTextField
      formik={formik}
      label={formMsg.ruleName}
      name='name'
      type='text'
    />
  );

  const columnField = (
    <FormikSelectField
      formik={formik}
      options={columnOptions}
      name='column'
      label={formMsg.colorizedColumn}
      searchable
    />
  );

  const groupsField = (
    <FormikMultiSelectField
      formik={formik}
      options={groupOptions}
      name='groups'
      label={formMsg.dashboardWidgets}
      searchable
    />
  );

  const descriptionField = (
    <FormikTextField
      formik={formik}
      label={formMsg.description}
      name={nestedFormikName('descriptions.general')}
      type='text'
    />
  );

  return (
    <div
      className={classNames(
        'rounded-lg shadow p mb-xl bg-default',
        props.className,
      )}
    >
      <div className='text-2xl'>{headingMsg.title}</div>
      <div className='pb-8 text-sm'>{headingMsg.subtitle}</div>
      <div className='flex flex-col lg:flex-row'>
        <div className='hidden w-80 lg:block'>
          {nameField}
          {columnField}
          {groupsField}
          {descriptionField}
        </div>
        <div className='flex lg:hidden'>
          <div className='w-2/4 flex-1 pr-4'>{nameField}</div>
          <div className='w-2/4 flex-1 pl-4'>{groupsField}</div>
        </div>
        <div className='flex lg:hidden'>
          <div className='w-2/4 flex-1 pr-4'>{columnField}</div>
          <div className='w-2/4 flex-1 pl-4'>{descriptionField}</div>
        </div>
        <div className='flex-1 lg:ml-12'>
          <Tabs
            variant='compact'
            tabs={tabsHook.tabs}
            onChangeTab={tabsHook.handleChangeTab}
            selected={tabsHook.activeTab}
          />
          {tabsHook.renderActiveTab()}
        </div>
      </div>
      <Button
        type='button'
        className='mr-2'
        onClick={() => {
          props.closeForm();
        }}
        variant='secondary'
        icon='fa-xmark'
      >
        {actionsMsg.cancel}
      </Button>
      {props.onDeleteClick && id && (
        <Button
          type='button'
          className='mr-2'
          onClick={() => {
            props.onDeleteClick?.({ id, name: formik.values.name });
          }}
          variant='secondary'
          accent='negative'
          icon='fa-trash-can'
        >
          {actionsMsg.delete}
        </Button>
      )}
      <Button
        type='button'
        onClick={submitForm}
        loading={loadingState === 'LOADING'}
        variant='primary'
        icon='fa-floppy-disk'
      >
        {id ? actionsMsg.updateRule : actionsMsg.createRule}
      </Button>
    </div>
  );
};
